import { useQuery } from '@tanstack/react-query';
import { getMatchProps, type MatchProps } from '../api';

function pct(v: number): string {
  if (!Number.isFinite(v)) return '—';
  if (v < 0.001) return '<0.1%';
  return `${(v * 100).toFixed(1)}%`;
}

export default function MatchPropsPanel({ matchId, homeName, awayName }: { matchId: string; homeName: string; awayName: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['match-props', matchId],
    queryFn: () => getMatchProps(matchId),
    refetchInterval: 60_000,
  });
  if (isLoading) return <div className="card p-4 text-xs text-dim">Loading props…</div>;
  if (!data?.props) return null;
  const p = data.props;

  return (
    <section className="card p-4 space-y-3">
      <div className="flex items-baseline justify-between">
        <h2 className="text-xs uppercase tracking-widest text-dim">All props · derived</h2>
        <span className="text-[10px] text-dim">xG H {p.expectedGoals.home.toFixed(2)} · A {p.expectedGoals.away.toFixed(2)}</span>
      </div>

      <SubSection title="Total goals (Over)">
        <Grid items={p.totals.map((t) => ({ label: `O ${t.line}`, value: pct(t.over) }))} />
      </SubSection>

      <SubSection title="Double chance">
        <Grid items={[
          { label: '1X', sub: `${homeName} or Draw`, value: pct(p.doubleChance['1X']) },
          { label: '12', sub: 'No draw', value: pct(p.doubleChance['12']) },
          { label: 'X2', sub: `Draw or ${awayName}`, value: pct(p.doubleChance['X2']) },
        ]} />
      </SubSection>

      <SubSection title="Both teams to score">
        <Grid items={[
          { label: 'Yes', value: pct(p.btts.yes) },
          { label: 'No', value: pct(p.btts.no) },
        ]} />
      </SubSection>

      <SubSection title="Clean sheet">
        <Grid items={[
          { label: homeName, value: pct(p.cleanSheet.home) },
          { label: awayName, value: pct(p.cleanSheet.away) },
        ]} />
      </SubSection>

      <SubSection title="Win to nil">
        <Grid items={[
          { label: homeName, value: pct(p.winToNil.home) },
          { label: awayName, value: pct(p.winToNil.away) },
        ]} />
      </SubSection>

      <SubSection title="Most likely scorelines">
        <Grid items={p.topCorrectScores.map((s) => ({ label: `${s.home}-${s.away}`, value: pct(s.prob) }))} />
      </SubSection>

      <p className="text-[10px] text-dim">Derived from bookmaker 1 X 2 + Over/Under via Poisson goal model. Independent-goals approximation.</p>
    </section>
  );
}

function SubSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-dim mb-1.5">{title}</div>
      {children}
    </div>
  );
}

function Grid({ items }: { items: { label: string; sub?: string; value: string }[] }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
      {items.map((it, i) => (
        <div key={i} className="rounded-md bg-ink-800/80 px-2 py-1.5 text-center">
          <div className="text-[10px] text-dim truncate">{it.label}</div>
          {it.sub && <div className="text-[10px] text-dim/80 truncate">{it.sub}</div>}
          <div className="stat text-sm text-chalk">{it.value}</div>
        </div>
      ))}
    </div>
  );
}
